import * as THREE from 'three';
import type { World, PlacedStructure } from '../world/World';
import type { Player } from '../player/Player';
import { buildPieces, variantsFor, GRID, type BuildPieceDef } from '../data/build';
import { itemDef } from '../data/items';
import { buildStructureMesh } from '../world/World';

/**
 * Yapı sistemi.
 * Mobilde kolay olması için: parçalar ızgaraya OTOMATİK oturur, önizleme hayaleti
 * gösterilir ve tek dokunuşla kurulur. Çekiç ile kırmadan yükseltme/varyant değişimi yapılır.
 */

const PLACE_DISTANCE = 6;

export interface PlacePlan {
  piece: BuildPieceDef;
  x: number;
  y: number;
  z: number;
  rotY: number;
  valid: boolean;
  reason?: string;
}

/** Oyuncunun baktığı yere ızgaraya oturmuş yerleşim planı üretir. */
export function planPlacement(world: World, player: Player, pieceId: string, rotationSteps: number): PlacePlan | null {
  const piece = buildPieces.get(pieceId);
  if (!piece) return null;

  const dir = player.forward();
  const origin = player.eyePosition;
  // Bakış yönünde ileri bir hedef noktası; zemine izdüşüm
  const dist = Math.min(PLACE_DISTANCE, 3.2 + Math.max(0, -dir.y) * 3);
  const tx = origin.x + dir.x * dist;
  const tz = origin.z + dir.z * dist;

  const rotY = (rotationSteps % 4) * (Math.PI / 2);
  const isWallLike = piece.shape === 'wall' || piece.shape === 'fence';

  let x: number;
  let z: number;
  if (isWallLike) {
    // Duvarlar hücre KENARINA oturur: böylece gerçek odalar kurulur
    const alongX = Math.abs(Math.cos(rotY)) > 0.5;
    if (alongX) {
      x = Math.round(tx / GRID) * GRID;
      z = Math.round((tz - GRID / 2) / GRID) * GRID + GRID / 2;
    } else {
      x = Math.round((tx - GRID / 2) / GRID) * GRID + GRID / 2;
      z = Math.round(tz / GRID) * GRID;
    }
  } else {
    x = Math.round(tx / GRID) * GRID;
    z = Math.round(tz / GRID) * GRID;
  }

  // Yükseklik seçimi:
  //  - Oyuncu arazide duruyorsa parça HEDEF HÜCRENİN arazisine oturur (araziyi takip eder).
  //    Aksi halde eğimli arazide "çok yüksek" diye reddedilir ve inşaat imkânsızlaşır.
  //  - Oyuncu bir yapının üstünde duruyorsa o kata hizalanır (kat kat inşaat).
  const terrain = world.heightAt(x, z);
  const playerTerrain = world.heightAt(player.position.x, player.position.z);
  const support = world.supportHeightAt(player.position.x, player.position.z, player.position.y);
  const onStructure = support > playerTerrain + 0.4;
  let y = onStructure ? support : terrain;
  if (piece.shape === 'ceiling') y += 3;

  const plan: PlacePlan = { piece, x, y, z, rotY, valid: true };

  // Su üstüne yapı (iskele) serbest; ama çok derin suya değil
  if (terrain < -6) {
    plan.valid = false;
    plan.reason = 'Su çok derin';
    return plan;
  }
  // Kat üstünde inşa ederken arazi ile fark çok açıldıysa havada asılı kalmasın
  if (onStructure && piece.shape !== 'ceiling' && y - terrain > 9) {
    plan.valid = false;
    plan.reason = 'Zeminden çok yüksek';
    return plan;
  }
  // Çok dik yamaca parça oturtma (görsel olarak havada kalır)
  if (!onStructure && world.slopeAt(x, z) > 1.6) {
    plan.valid = false;
    plan.reason = 'Yamaç çok dik';
    return plan;
  }
  // Oyuncunun içine duvar kurulmasın
  if (piece.blocks) {
    const dx = player.position.x - x;
    const dz = player.position.z - z;
    const c = Math.cos(rotY);
    const s = Math.sin(rotY);
    const lx = dx * c - dz * s;
    const lz = dx * s + dz * c;
    if (Math.abs(lx) < GRID / 2 + 0.4 && Math.abs(lz) < 0.8 && player.position.y + 1.7 > y && player.position.y < y + 3) {
      plan.valid = false;
      plan.reason = 'Kendi üstüne kuramazsın';
      return plan;
    }
  }
  return plan;
}

export type BuildResult = { ok: true; structure: PlacedStructure } | { ok: false; reason: string };

/** Planı uygular: envanterden yerleştirilebilir item düşülür. */
export function place(world: World, player: Player, plan: PlacePlan): BuildResult {
  if (!plan.valid) return { ok: false, reason: plan.reason ?? 'Buraya kurulamaz' };
  // Ekin parçaları doğrudan tohum item'ını tüketir; diğer yapılar craft edilmiş
  // place_* item'ını ister.
  const itemId = plan.piece.shape === 'crop'
    ? (plan.piece.id === 'crop_wheat' ? 'wheat_seed' : 'berry_seed')
    : `place_${plan.piece.id}`;
  if (!player.inventory.has(itemId, 1)) return { ok: false, reason: `${plan.piece.name} yok` };

  // Mutasyon komut darboğazından geçer (çok oyunculu hazırlığı)
  const res = world.apply({ t: 'structure:place', piece: plan.piece.id, x: plan.x, y: plan.y, z: plan.z, rotY: plan.rotY });
  if (!res.ok) return { ok: false, reason: res.reason ?? 'Buraya kurulamaz' };
  player.inventory.remove(itemId, 1);
  return { ok: true, structure: res.data as PlacedStructure };
}

/** Çekiçle bakılan yapıyı bulur. */
export function pickStructure(world: World, player: Player): PlacedStructure | null {
  const raycaster = new THREE.Raycaster();
  raycaster.far = PLACE_DISTANCE;
  raycaster.set(player.eyePosition, player.forward());
  const hits = raycaster.intersectObjects([...world.pickables()], true);
  for (const hit of hits) {
    let obj: THREE.Object3D | null = hit.object;
    while (obj && !obj.userData.structureId) obj = obj.parent;
    if (obj?.userData.structureId) {
      const st = world.structureById(obj.userData.structureId as string);
      if (st) return st;
    }
  }
  return null;
}

export interface HammerOption {
  id: string;
  label: string;
  kind: 'variant' | 'upgrade' | 'repair' | 'remove';
  cost?: Array<{ item: string; count: number }>;
  targetPiece?: string;
}

/** Çekiç menüsü seçenekleri (yapıyı kırmadan dönüştürme). */
export function hammerOptions(world: World, st: PlacedStructure): HammerOption[] {
  const def = buildPieces.get(st.piece);
  if (!def) return [];
  const out: HammerOption[] = [];

  for (const v of variantsFor(def.id)) {
    if (v.id === def.id) continue;
    out.push({ id: `variant_${v.id}`, label: v.name, kind: 'variant', targetPiece: v.id, cost: extraCost(def, v) });
  }
  if (def.upgradeTo) {
    const up = buildPieces.get(def.upgradeTo);
    if (up) out.push({ id: `upgrade_${up.id}`, label: `${up.name} (yükselt)`, kind: 'upgrade', targetPiece: up.id, cost: extraCost(def, up) });
  }
  if (st.hp < def.durability) {
    out.push({ id: 'repair', label: `Tamir et (${Math.round((st.hp / def.durability) * 100)}%)`, kind: 'repair', cost: repairCost(def, st) });
  }
  out.push({ id: 'remove', label: 'Sök (malzeme geri)', kind: 'remove' });
  void world;
  return out;
}

/** Yükseltme/varyant farkı kadar malzeme ister. */
function extraCost(from: BuildPieceDef, to: BuildPieceDef): Array<{ item: string; count: number }> {
  const have = new Map<string, number>();
  for (const c of from.cost) have.set(c.item, (have.get(c.item) ?? 0) + c.count);
  const out: Array<{ item: string; count: number }> = [];
  for (const c of to.cost) {
    const already = have.get(c.item) ?? 0;
    const need = Math.max(0, c.count - already);
    if (need > 0) out.push({ item: c.item, count: need });
  }
  return out;
}

function repairCost(def: BuildPieceDef, st: PlacedStructure): Array<{ item: string; count: number }> {
  const missing = 1 - st.hp / def.durability;
  return def.cost.map((c) => ({ item: c.item, count: Math.max(1, Math.ceil(c.count * missing * 0.5)) }));
}

export type HammerResult = { ok: true; message: string } | { ok: false; reason: string };

export function applyHammer(world: World, player: Player, st: PlacedStructure, option: HammerOption): HammerResult {
  const def = buildPieces.get(st.piece);
  if (!def) return { ok: false, reason: 'Yapı tanınmıyor' };

  // Maliyet kontrolü
  for (const c of option.cost ?? []) {
    if (!player.inventory.has(c.item, c.count)) return { ok: false, reason: `Yetersiz: ${itemDef(c.item).name}` };
  }

  if (option.kind === 'remove') {
    world.apply({ t: 'structure:remove', id: st.id });
    // Malzemenin yarısı geri (sökme kaybı)
    for (const c of def.cost) player.inventory.add(c.item, Math.max(1, Math.floor(c.count / 2)));
    return { ok: true, message: `${def.name} söküldü` };
  }

  for (const c of option.cost ?? []) player.inventory.remove(c.item, c.count);

  if (option.kind === 'repair') {
    world.apply({ t: 'structure:repair', id: st.id, amount: def.durability });
    return { ok: true, message: `${def.name} tamir edildi` };
  }

  const target = buildPieces.get(option.targetPiece ?? '');
  if (!target) return { ok: false, reason: 'Hedef parça yok' };
  world.apply({ t: 'structure:replace', id: st.id, piece: target.id });
  return { ok: true, message: `${target.name} oldu` };
}

/** Yerleştirme hayaleti (yeşil = geçerli, kırmızı = geçersiz). */
export class PlacementGhost {
  private current: string | null = null;
  private mesh: THREE.Object3D | null = null;
  private readonly validMat = new THREE.MeshBasicMaterial({ color: 0x4ade80, transparent: true, opacity: 0.45, depthWrite: false });
  private readonly invalidMat = new THREE.MeshBasicMaterial({ color: 0xef4444, transparent: true, opacity: 0.4, depthWrite: false });

  constructor(private root: THREE.Object3D) {}

  update(plan: PlacePlan | null): void {
    if (!plan) {
      if (this.mesh) this.mesh.visible = false;
      return;
    }
    if (this.current !== plan.piece.id) {
      if (this.mesh) {
        this.root.remove(this.mesh);
      }
      this.mesh = buildStructureMesh(plan.piece);
      this.mesh.traverse((o) => {
        if (o instanceof THREE.Mesh) o.material = this.validMat;
      });
      this.root.add(this.mesh);
      this.current = plan.piece.id;
    }
    if (!this.mesh) return;
    this.mesh.visible = true;
    this.mesh.position.set(plan.x, plan.y, plan.z);
    this.mesh.rotation.y = plan.rotY;
    const mat = plan.valid ? this.validMat : this.invalidMat;
    this.mesh.traverse((o) => {
      if (o instanceof THREE.Mesh) o.material = mat;
    });
  }

  hide(): void {
    if (this.mesh) this.mesh.visible = false;
  }
}
